// ErrorCard organism — extracted from MessageBubble.
//
// Renders error messages with a red-tinted card.

import type { Message } from '../../types';
import { asError } from '../../lib/messages';
import { useTranslation } from '../../i18n';
import { DeveloperOnly } from '../founder/DeveloperOnly';

export interface ErrorCardProps {
  message: Message;
}

export function ErrorCard({ message }: ErrorCardProps) {
  const { t } = useTranslation();
  const c = asError(message);
  // Sprint messages-section-fixes (D-msg-17) — `null` is treated as
  // "no detail", same as `undefined`. Pre-fix, `c.detail === null` was
  // run through `safeJson` and produced the literal string `"null"`,
  // which then passed the truthy `{detail && <pre>…</pre>}` guard and
  // rendered the word "null" inside the error card.
  const detail =
    typeof c.detail === 'string'
      ? c.detail
      : c.detail !== undefined && c.detail !== null
        ? safeJson(c.detail)
        : '';
  return (
    <div
      className="fade-in-0 slide-in-from-top-2 animate-in rounded-lg border border-status-error/40 bg-status-error/10 px-3 py-2 text-body text-status-error"
      data-testid="error-message"
    >
      <div className="font-medium mb-1">
        {c.reason
          ? t('error.agentFailedWithReason', { reason: c.reason })
          : t('error.agentFailed')}
      </div>
      {/* Sprint messages-section-fixes (D-msg-12) — raw error detail
          (stack trace / serialized object) is developer-shaped. The
          founder already has the actionable signal in the headline
          ("Agent failed: <reason>"); the technical body lives behind
          the developer toggle. */}
      {detail && (
        <DeveloperOnly>
          <pre className="text-xs whitespace-pre-wrap break-words text-status-error/80"> {/* design-discipline: kept as text-xs — developer-mode error detail */}
            {detail}
          </pre>
        </DeveloperOnly>
      )}
    </div>
  );
}

function safeJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
